import React, { useState } from 'react';
import { useLocation } from 'react-router-dom'
import { TextInput, Textarea, Button } from '@mantine/core';
import { ContactIconsList } from './ContactIcons';
import { useForm } from '@mantine/form';
import api from '../../api/axios';

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

// Dark fields with a light border on focus, matching the site's black and zinc palette
const fieldStyles = {
    label: { color: '#d4d4d8', marginBottom: 6 },
    required: { color: '#a1a1aa' },
    input: {
        backgroundColor: '#141517',
        borderColor: '#3f3f46',
        color: '#ffffff',
        fontSize: 16, // 16px keeps iOS from zooming in on focus
        '&::placeholder': { color: '#71717a' },
        '&:focus, &:focus-within': { borderColor: '#ffffff' },
    },
};

const Contact = () => {
    const location = useLocation();
    const item = location.state?.item;
    const [sentTo, setSentTo] = useState(null); // { name, email } once a message is sent
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [website, setWebsite] = useState(''); // honeypot, hidden from people

    const form = useForm({
        initialValues: { name: '', email: '', message: item ? `Hi Juli, I'm interested in "${item}". How can I purchase it?` : '' },
        validate: (values) => ({
            name:
                values.name.trim().length === 0
                    ? 'Please enter your name'
                    : null,
            email:
                values.email.length === 0
                    ? 'Please enter your email'
                    : EMAIL_REGEX.test(values.email.trim())
                        ? null
                        : 'Please enter a valid email address',
            message:
                values.message.trim().length === 0
                    ? 'Please enter a message'
                    : null,
        }),
    });

    const handleSubmit = async (values) => {
        setSendError(null);
        setSending(true);
        try {
            await api.post('/contact-form', { ...values, website });
            setSentTo({ name: values.name.trim(), email: values.email.trim() });
            form.reset();
        } catch (err) {
            setSendError(err.response?.data?.message || "Your message couldn't be sent. Please reach out by phone, text or email instead.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className='flex w-full pb-16 justify-center'>
            <div className='w-full flex flex-col 820:max-w-[820px] 520:max-w-[500px] max-w-[400px] px-4 520:px-0'>
                <div className='flex text-white py-8 text-2xl font-light uppercase'>
                    Contact
                </div>

                <div className='grid grid-cols-1 820:grid-cols-2 gap-10 820:gap-12'>
                    <div className='flex flex-col gap-8'>
                        <div className='text-zinc-300 leading-relaxed'>
                            Let me bring your ideas to life. For commissions, memorial pieces, installations
                            or pieces from the shop, send me a message here or reach out by phone, text or email.
                        </div>
                        <ContactIconsList />
                    </div>

                    {sentTo
                        ? <div role='status' className='flex flex-col gap-3 rounded-lg border border-solid border-zinc-800 p-8'>
                            <div className='text-white text-xl font-light uppercase tracking-wide'>Message sent</div>
                            <div className='text-zinc-300 leading-relaxed'>
                                Thanks, {sentTo.name}. Juli will get back to you at <span className='text-white'>{sentTo.email}</span>.
                            </div>
                            <button
                                type='button'
                                onClick={() => setSentTo(null)}
                                style={{ fontFamily: 'inherit' }}
                                className='self-start mt-2 p-0 bg-transparent border-0 cursor-pointer text-zinc-400 hover:text-white underline underline-offset-4 text-sm'
                            >
                                Send another message
                            </button>
                        </div>
                        : <form onSubmit={form.onSubmit((values) => handleSubmit(values))} className='flex flex-col gap-5' noValidate>
                            <TextInput
                                label='Name'
                                placeholder='Your name'
                                withAsterisk
                                size='md'
                                autoComplete='name'
                                styles={fieldStyles}
                                {...form.getInputProps('name')}
                            />
                            <TextInput
                                label='Email'
                                placeholder='you@example.com'
                                withAsterisk
                                size='md'
                                type='email'
                                autoComplete='email'
                                styles={fieldStyles}
                                {...form.getInputProps('email')}
                            />
                            <Textarea
                                label='Message'
                                placeholder="Tell me about the piece you're interested in"
                                withAsterisk
                                size='md'
                                minRows={5}
                                autosize
                                styles={fieldStyles}
                                {...form.getInputProps('message')}
                            />
                            <input
                                type='text'
                                name='website'
                                value={website}
                                onChange={(event) => setWebsite(event.currentTarget.value)}
                                tabIndex={-1}
                                autoComplete='off'
                                aria-hidden='true'
                                style={{ position: 'absolute', left: '-10000px' }}
                            />

                            <Button
                                type='submit'
                                size='md'
                                fullWidth
                                loading={sending}
                                styles={{
                                    root: {
                                        backgroundColor: '#ffffff',
                                        color: '#000000',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        '&:not([data-disabled]):hover': { backgroundColor: '#e4e4e7' },
                                        '&[data-loading]': { backgroundColor: '#d4d4d8', color: '#000000' },
                                    },
                                }}
                            >
                                {sending ? 'Sending' : 'Send message'}
                            </Button>

                            {sendError && (
                                <div role='alert' className='text-red-400 text-sm'>
                                    {sendError}
                                </div>
                            )}
                        </form>
                    }
                </div>
            </div>
        </div>
    );
}

export default Contact
